import { ethers } from 'ethers';

// Contract ABI (should match your deployed contract)
const contractABI = [
  "function mint(address to, uint256 amount) public",
  "function balanceOf(address account) public view returns (uint256)",
  "function transfer(address to, uint256 amount) public returns (bool)",
  "function totalSupply() public view returns (uint256)",
  "function name() public view returns (string)",
  "function symbol() public view returns (string)"
];

// Network configurations
const networks = {
  'polygon-mumbai': {
    chainId: '0x13881',
    chainName: 'Polygon Mumbai',
    nativeCurrency: { name: 'MATIC', symbol: 'MATIC', decimals: 18 },
    rpcUrls: ['https://polygon-mumbai.infura.io/v3/9aa3d95b3bc440fa88ea12eaa4456161'],
    blockExplorerUrls: ['https://mumbai.polygonscan.com']
  }
};

class BlockchainService {
  constructor() {
    this.provider = null;
    this.signer = null;
    this.contract = null;
    this.contractAddress = import.meta.env.VITE_REACT_APP_CONTRACT_ADDRESS || '';
  }

  // Initialize blockchain connection
  async initialize() {
    if (!window.ethereum) {
      throw new Error('MetaMask is not installed');
    }

    this.provider = new ethers.BrowserProvider(window.ethereum);
    this.signer = await this.provider.getSigner();

    if (this.contractAddress) {
      this.contract = new ethers.Contract(this.contractAddress, contractABI, this.signer);
    }
  }

  // Connect wallet
  async connectWallet() {
    if (!window.ethereum) {
      throw new Error('MetaMask is not installed');
    }

    try {
      const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
      await this.initialize();
      return accounts[0];
    } catch (error) {
      throw new Error('Failed to connect wallet: ' + error.message);
    }
  }

  // Switch to Polygon Mumbai network
  async switchToPolygonMumbai() {
    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: networks['polygon-mumbai'].chainId }]
      });
    } catch (error) {
      // If network doesn't exist, add it
      if (error.code === 4902) {
        await window.ethereum.request({
          method: 'wallet_addEthereumChain',
          params: [networks['polygon-mumbai']]
        });
      } else {
        throw error;
      }
    }
  }

  // Get wallet balance
  async getBalance(address) {
    const balance = await this.provider.getBalance(address);
    return ethers.formatEther(balance);
  }

  // Get token balance
  async getTokenBalance(address) {
    if (!this.contract) throw new Error('Contract not initialized');
    const balance = await this.contract.balanceOf(address);
    return ethers.formatEther(balance);
  }

  // Mint tokens (only owner can call this)
  async mintTokens(toAddress, amount) {
    if (!this.contract) throw new Error('Contract not initialized');

    try {
      const tx = await this.contract.mint(toAddress, ethers.parseEther(amount.toString()));
      await tx.wait();
      return tx.hash;
    } catch (error) {
      throw new Error('Failed to mint tokens: ' + error.message);
    }
  }

  // Transfer tokens
  async transferTokens(toAddress, amount) {
    if (!this.contract) throw new Error('Contract not initialized');

    try {
      const tx = await this.contract.transfer(toAddress, ethers.parseEther(amount.toString()));
      await tx.wait();
      return tx.hash;
    } catch (error) {
      throw new Error('Failed to transfer tokens: ' + error.message);
    }
  }

  // Get contract info
  async getContractInfo() {
    if (!this.contract) throw new Error('Contract not initialized');

    const [name, symbol, totalSupply] = await Promise.all([
      this.contract.name(),
      this.contract.symbol(),
      this.contract.totalSupply()
    ]);

    return {
      name,
      symbol,
      totalSupply: ethers.formatEther(totalSupply),
      address: this.contractAddress
    };
  }

  // Get network info
  async getNetworkInfo() {
    const network = await this.provider.getNetwork();
    const blockNumber = await this.provider.getBlockNumber();
    const gasPrice = await this.provider.getFeeData();

    return {
      chainId: network.chainId,
      name: network.name,
      blockNumber,
      gasPrice: ethers.formatUnits(gasPrice.gasPrice, 'gwei')
    };
  }

  // Listen for account changes
  onAccountChanged(callback) {
    if (window.ethereum) {
      window.ethereum.on('accountsChanged', callback);
    }
  }

  // Listen for chain changes
  onChainChanged(callback) {
    if (window.ethereum) {
      window.ethereum.on('chainChanged', callback);
    }
  }
}

export default new BlockchainService();
